// src/services/pushNotifications.js
//
// Native push notifications (iOS/Android) via @capacitor-firebase/messaging.
// ENTIRELY native-gated: every exported function is a no-op on web, and the
// plugin is imported dynamically so it never runs in the web bundle. Mirrors
// the dynamic-import + Capacitor.isNativePlatform() pattern in
// services/firebasePhoneAuth.js. Token is persisted server-side via the
// storePushToken callable; sending is handled by the sendPushOnNotification
// Cloud Function (one onCreate trigger over users/{uid}/notifications).

import { Capacitor } from '@capacitor/core';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase';
import { scrollPageToTop } from '../utils/scrollPageToTop';
import { trace } from '../utils/traceLog';
import { isMultiMemberInterestDigest, INTERESTS_TAB_PATH } from '../utils/tradeCenterTab';

// React Router navigate fn, registered from App.js (setPushNavigator). Lets the
// tap handler route while the app stays mounted; falls back to window.location.
let navigateFn = null;

export function setPushNavigator(fn) {
  navigateFn = fn;
}

// Mirrors NotificationDropdown.handleNotificationClick routing, but reads the
// flat string fields FCM delivers in the data payload (FCM data values are
// always strings). Kept in sync with the bell so taps land on the same page.
export function resolveTargetUrl(data = {}) {
  const { type, tradeId, purchaseId, offerId, matchId, action, listingId, interestId, outcome } = data;
  switch (type) {
    case 'listing_interest':
      // Batched inbound interest: one row per listing digest. Multi-member
      // digests open the Trade Center's Interests tab; a solo signal still
      // opens that member's board directly.
      if (isMultiMemberInterestDigest(data)) {
        return INTERESTS_TAB_PATH;
      }
      // An owner's reply ("interested") is waiting as a Ready-for-an-offer card
      // on the Interests tab.
      if (outcome === 'interested') return INTERESTS_TAB_PATH;
      if (interestId) return `/interest/${interestId}`;
      return '/trade-center';
    case 'new_message':
      if (tradeId) return `/trade-messages/${tradeId}`;
      if (purchaseId) return `/purchase-messages/${purchaseId}`;
      return '/trade-center';
    case 'listing_comment':
    case 'comment_mention':
      if (listingId) return `/listing/${listingId}?scrollToComments=true`;
      return '/';
    case 'new_offer':
    case 'offer_comment':
      return tradeId ? `/offer/${tradeId}` : '/trade-center';
    case 'offer_status_change':
      if (action === 'accepted') return '/accepted-trades';
      return tradeId ? `/offer/${tradeId}` : '/trade-center';
    case 'offer_viewed':
    case 'offer_expired':
      if (tradeId) return `/offer/${tradeId}`;
      if (offerId) return `/purchase-offer-details/${offerId}`;
      return '/trade-center';
    case 'new_match':
    case 'match_comment':
      return matchId ? `/match/${matchId}` : '/matches';
    case 'new_match_batch':
      return '/matches';
    case 'want_list_match':
      return listingId ? `/listing/${listingId}` : '/want-list-matches';
    case 'step_reminder':
      if (tradeId) return `/trade-progress/${tradeId}`;
      if (purchaseId) return `/purchase-progress/${purchaseId}`;
      return '/trade-center';
    case 'ready_to_ship':
    case 'trade_complete':
    case 'tracking_added_trade':
    case 'package_delivered_trade':
      return tradeId ? `/trade-progress/${tradeId}` : '/trade-center';
    case 'purchase_complete':
    case 'tracking_added_purchase':
    case 'package_delivered_purchase':
      return purchaseId ? `/purchase-progress/${purchaseId}` : '/trade-center';
    case 'new_purchase_offer':
      return offerId ? `/purchase-offer-details/${offerId}` : '/trade-center';
    case 'purchase_offer_status_change':
      if (action === 'accepted') return '/accepted-purchases';
      return offerId ? `/purchase-offer-details/${offerId}` : '/trade-center';
    default:
      return '/trade-center';
  }
}

function navigateTo(url) {
  if (navigateFn) {
    navigateFn(url);
    // A push tap teleports the user to fresh content, but when the target is
    // the route they're ALREADY on the router doesn't remount — the page kept
    // the old scroll offset (and pages without their own mount-scroll inherit
    // it on route changes too, since the SPA has no router scroll
    // restoration). Found during Android testing, 2026-08-20.
    scrollPageToTop({ blur: false });
  } else {
    // App not yet mounted with a navigator (e.g. cold start from a tapped
    // push): hard-load the route. Capacitor's SPA fallback serves index.html.
    window.location.assign(url);
  }
}

// ── Registration lifecycle (2026-10-07) ─────────────────────────────────────
// A reinstalled iPhone sat with no notification permission and nothing in the
// app noticed: the account kept two stale tokens Apple still accepted, so the
// server logged "2/2 sent" while the phone got nothing. The review of that
// incident found the old registration path could also bind listeners twice
// (listenersBound was set only after three awaited addListener calls), resume
// after logout, and fail silently. The rules below close those gaps:
//
// - SESSION: App.js calls setPushSession(uid) whenever the signed-in uid
//   changes (login, logout, account switch). Every change bumps a generation;
//   work started under an older generation is abandoned before it persists.
// - ONE ATTEMPT PER SESSION: concurrent callers share the in-flight attempt.
//   A caller that wants the OS prompt while a silent attempt is running waits
//   for it, then runs its own attempt if the silent one found no permission.
// - EXPECTED USER: a token is stored only if the session uid still matches
//   auth.currentUser (tokenReceived refreshes included).
// - LISTENERS: bound once through a shared promise; if binding fails partway,
//   the handles already attached are removed so a retry starts clean.
// - RESULTS are distinct, so callers never report success merely because
//   permission is granted. Traces carry the result code only, never the token.

export const REGISTER_RESULTS = Object.freeze({
  REGISTERED: 'registered',
  PERMISSION_NOT_GRANTED: 'permission_not_granted',
  // Permission granted, but the listener bind, token fetch or server store failed.
  REGISTRATION_FAILED: 'registration_failed',
  // The permission check or the OS request itself threw: permission unknown.
  PERMISSION_FAILED: 'permission_failed',
  NO_SESSION: 'no_session',
  STALE: 'stale',
  NOT_NATIVE: 'not_native',
});

const PROMPTABLE = new Set(['prompt', 'prompt-with-rationale']);

let sessionUid = null;
let generation = 0;
let inFlight = null; // { gen, promptIfNeeded, promise }
let listenersPromise = null;
// The latest attempt's result for the CURRENT session, published so Profile
// can show the real outcome of a registration it didn't start (App.js
// registers at login). Reset when the session changes.
let lastResult = null;
const resultListeners = new Set();
// Logout cleanup (listener removal + token deletion) in progress. New
// registrations wait for it, so cleanup can't remove a newer session's
// listeners or delete its fresh token.
let cleanupBarrier = Promise.resolve();

// Publish a result only if it still belongs to the current session, checked
// AFTER any async write so a late result can't land on a newer account.
function publish(gen, uid, result) {
  if (result === REGISTER_RESULTS.STALE || !isCurrent(gen, uid)) return;
  lastResult = result;
  resultListeners.forEach((fn) => { try { fn(result); } catch (e) { /* listener's problem */ } });
}

export function getLastRegisterResult() {
  return lastResult;
}

export function subscribeRegisterResult(fn) {
  resultListeners.add(fn);
  return () => resultListeners.delete(fn);
}

// Test seam: lets tests supply the plugin and current-user lookup without a
// native runtime. Production uses the dynamic import and firebase auth.
//
// NEVER resolve a promise with the plugin itself (2026-10-08, found on a
// real iPhone): Capacitor plugins are Proxies that return a method for ANY
// property, including `then` (@capacitor/core 8.4.1 special-cases only
// $$typeof, toJSON, addListener, removeListener). Returning the plugin from
// an async function makes JS treat it as a thenable and wait on a native
// "then" call that never answers, so every permission check and
// registration hung silently. Resolve with the MODULE and destructure.
// Callers therefore always write `const { FirebaseMessaging } = await
// loadPluginModule();`, never an async helper that returns the plugin.
let loadPluginModule = () => import('@capacitor-firebase/messaging');
let currentUid = () => (auth.currentUser ? auth.currentUser.uid : null);
export function __setPushTestSeams({ plugin, getCurrentUid } = {}) {
  if (plugin) loadPluginModule = async () => ({ FirebaseMessaging: plugin });
  if (getCurrentUid) currentUid = getCurrentUid;
}
export function __resetPushStateForTests() {
  sessionUid = null;
  generation += 1;
  inFlight = null;
  listenersPromise = null;
  lastResult = null;
  resultListeners.clear();
  cleanupBarrier = Promise.resolve();
}

function isCurrent(gen, uid) {
  return gen === generation && uid != null && uid === sessionUid && currentUid() === uid;
}

/**
 * Bumps on every session change, including A → B → A, so a caller can tell
 * that "the same uid again" is still a different session.
 */
export function getPushSessionGeneration() {
  return generation;
}

/** Called by App.js whenever the signed-in uid changes (null on logout). */
export function setPushSession(uid) {
  const next = uid || null;
  if (next === sessionUid) return;
  sessionUid = next;
  generation += 1;
  inFlight = null;
  lastResult = null;
  trace('push:session', { signedIn: next != null });
}

/**
 * The device's notification permission as the installed plugin reports it:
 * 'granted' | 'denied' | 'prompt' | 'prompt-with-rationale', or 'unknown'
 * for anything else and 'error' when the check throws. Never rejects.
 * 'prompt' is the plugin's mapping (iOS: notDetermined, and also any future
 * authorization state the plugin doesn't recognise; Android 13+: never
 * requested); it is not an absolute guarantee the user was never asked.
 * On Android 12 and older the plugin always reports 'granted'.
 */
export async function getPushPermissionState() {
  if (!Capacitor.isNativePlatform()) return 'unavailable';
  try {
    const { FirebaseMessaging } = await loadPluginModule();
    const perm = await FirebaseMessaging.checkPermissions();
    const state = perm && perm.receive;
    return ['granted', 'denied', 'prompt', 'prompt-with-rationale'].includes(state) ? state : 'unknown';
  } catch (err) {
    return 'error';
  }
}

async function persistToken(token, gen, uid) {
  if (!token) return REGISTER_RESULTS.REGISTRATION_FAILED;
  if (!isCurrent(gen, uid)) return REGISTER_RESULTS.STALE;
  try {
    const storePushToken = httpsCallable(functions, 'storePushToken');
    await storePushToken({ token, platform: Capacitor.getPlatform() });
    return REGISTER_RESULTS.REGISTERED;
  } catch (err) {
    console.warn('⚠️ [push] failed to store token:', err?.message || err);
    return REGISTER_RESULTS.REGISTRATION_FAILED;
  }
}

function bindListeners(FirebaseMessaging) {
  if (listenersPromise) return listenersPromise;
  let mine = null;
  mine = (async () => {
    const handles = [];
    try {
      handles.push(await FirebaseMessaging.addListener('tokenReceived', (event) => {
        // A refreshed token belongs to whoever is signed in NOW; skip it when
        // no session matches the signed-in user (logged out, mid-switch).
        const uid = sessionUid;
        const gen = generation;
        if (!isCurrent(gen, uid)) {
          trace('push:token_refresh', { result: REGISTER_RESULTS.STALE });
          return;
        }
        persistToken(event?.token, gen, uid).then((result) => {
          trace('push:token_refresh', { result });
          publish(gen, uid, result); // a failed refresh must reach Profile
        });
      }));
      handles.push(await FirebaseMessaging.addListener('notificationReceived', (event) => {
        // Foreground delivery. In-app bell is already updated by the live
        // NotificationContext Firestore listener, so no UI action here.
        console.log('🔔 [push] notificationReceived (foreground)', event?.notification?.title);
        trace('notif:received', { fg: true });
      }));
      handles.push(await FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
        const data = event?.notification?.data || {};
        console.log('👆 [push] notificationActionPerformed', data?.type);
        const route = resolveTargetUrl(data);
        // Wake-hang trace: position the tap relative to wake/reconnect in the
        // dump, and record whether this was a warm tap (router mounted) or a
        // cold-boot tap (mounted:false → hard load). No titles/body logged.
        trace('notif:tap', {
          type: String(data?.type || 'unknown'),
          route,
          mounted: !!navigateFn,
          vis: typeof document !== 'undefined' ? document.visibilityState : 'unknown',
        });
        navigateTo(route);
      }));
    } catch (err) {
      // Partial failure: remove what was attached so a retry binds cleanly
      // instead of stacking a second copy of the first listeners.
      await Promise.all(handles.map((h) => Promise.resolve().then(() => h && h.remove && h.remove()).catch(() => {})));
      if (listenersPromise === mine) listenersPromise = null; // never a newer bind's state
      throw err;
    }
  })();
  listenersPromise = mine;
  return mine;
}

async function attempt(gen, uid, promptIfNeeded) {
  if (!uid) return REGISTER_RESULTS.NO_SESSION;
  let FirebaseMessaging;
  let perm;
  // Permission phase: a throw here leaves permission UNKNOWN, which is not
  // a registration failure (review 2026-10-07): callers snooze and keep the
  // explicit "Turn on" path rather than offer a silent retry.
  try {
    ({ FirebaseMessaging } = await loadPluginModule());
    perm = await FirebaseMessaging.checkPermissions();
    if (!isCurrent(gen, uid)) return REGISTER_RESULTS.STALE;
    if (PROMPTABLE.has(perm && perm.receive)) {
      if (!promptIfNeeded) {
        // Defer the OS permission prompt to a contextual moment or an
        // explicit Profile action; never prompt at startup.
        return REGISTER_RESULTS.PERMISSION_NOT_GRANTED;
      }
      perm = await FirebaseMessaging.requestPermissions();
      if (!isCurrent(gen, uid)) return REGISTER_RESULTS.STALE;
    }
  } catch (err) {
    console.error('❌ [push] permission check/request failed:', err?.message || err);
    return REGISTER_RESULTS.PERMISSION_FAILED;
  }
  if (!perm || perm.receive !== 'granted') return REGISTER_RESULTS.PERMISSION_NOT_GRANTED;

  // Registration phase: permission is granted from here on.
  try {
    await bindListeners(FirebaseMessaging);
    if (!isCurrent(gen, uid)) return REGISTER_RESULTS.STALE;
    const { token } = await FirebaseMessaging.getToken();
    return await persistToken(token, gen, uid);
  } catch (err) {
    console.error('❌ [push] register failed:', err?.message || err);
    return REGISTER_RESULTS.REGISTRATION_FAILED;
  }
}

// Register for push. Resolves (never rejects) with a REGISTER_RESULTS code.
//
//   promptIfNeeded = false (default): startup/login use. Registers the token
//     only if permission is already granted; never shows the OS prompt.
//
//   promptIfNeeded = true: an explicit user action (the soft pop-up's "Turn
//     on", or Profile). Shows the OS prompt when the plugin reports 'prompt'
//     or 'prompt-with-rationale'.
export async function registerPushNotifications({ promptIfNeeded = false } = {}) {
  if (!Capacitor.isNativePlatform()) return REGISTER_RESULTS.NOT_NATIVE;
  const gen = generation;
  const uid = sessionUid;
  if (!uid) return REGISTER_RESULTS.NO_SESSION;

  // Let an earlier logout's cleanup finish first (listener removal, token
  // deletion), so it can't undo this session's registration.
  await cleanupBarrier;
  if (!isCurrent(gen, uid)) return REGISTER_RESULTS.STALE;

  if (inFlight && inFlight.gen === gen) {
    const shared = inFlight;
    const result = await shared.promise;
    // A caller that wants the OS prompt runs its own attempt only when the
    // shared one was silent and found no permission.
    const needOwn = promptIfNeeded && !shared.promptIfNeeded && result === REGISTER_RESULTS.PERMISSION_NOT_GRANTED;
    if (!needOwn) return result;
    if (!isCurrent(gen, uid)) return REGISTER_RESULTS.STALE;
    return registerPushNotifications({ promptIfNeeded });
  }

  const entry = { gen, promptIfNeeded, promise: null };
  entry.promise = attempt(gen, uid, promptIfNeeded).then((result) => {
    if (inFlight === entry) inFlight = null;
    trace('push:register', { result, prompt: promptIfNeeded });
    publish(gen, uid, result);
    return result;
  });
  inFlight = entry;
  return entry.promise;
}

// Logout cleanup: invalidate in-flight work, stop this device receiving
// pushes and drop listeners. Serialized through cleanupBarrier: repeated
// logouts chain, and registrations for the next session wait until the
// listeners are removed and the old token is deleted.
export async function unregisterPushNotifications() {
  if (!Capacitor.isNativePlatform()) return;
  setPushSession(null);
  const run = cleanupBarrier.then(async () => {
    try {
      const { FirebaseMessaging } = await loadPluginModule();
      // Wait for any bind in progress so its handles are removed too.
      if (listenersPromise) { try { await listenersPromise; } catch (e) { /* already cleaned */ } }
      await FirebaseMessaging.removeAllListeners();
      listenersPromise = null;
      try {
        await FirebaseMessaging.deleteToken();
      } catch (e) {
        // deleteToken can throw if no token exists yet; ignore.
      }
      console.log('✅ [push] unregistered');
    } catch (err) {
      console.warn('⚠️ [push] unregister failed:', err?.message || err);
    }
  });
  cleanupBarrier = run;
  await run;
}
